/**
 * Support-request shape and rules, shared by the form, the server function, and
 * the admin page. Isomorphic and secret-free — no server-only imports here.
 *
 * Spam defence is deliberately friction-free (no CAPTCHA): a honeypot field, a
 * minimum time-on-form, and a per-IP cap in the server fn. The first two are
 * ADVISORY signals the client sends, so they are only ever used to silently
 * DROP a submission — never to show an error, which would tell a bot author
 * exactly which check tripped. The per-IP cap is the only hard limit and it
 * lives server-side (see lib/support/server.ts).
 */
import { z } from 'zod'

import {
  requiredMultilineText,
  requiredText,
  email as zEmail,
} from '@/lib/validation'

export type SupportTopic = 'billing' | 'technical' | 'sales' | 'abuse' | 'other'

export type SupportMessageStatus = 'new' | 'handled'

/** Order here is the order in the picker. */
export const SUPPORT_TOPICS: {
  value: SupportTopic
  label: string
  /** Shown under the picker so people self-route instead of picking Other. */
  hint: string
}[] = [
  {
    value: 'billing',
    label: 'Billing',
    hint: 'Invoices, payments, plan changes, cancellation, refunds.',
  },
  {
    value: 'technical',
    label: 'Technical issue',
    hint: 'Connection problems, credentials, errors, unexpected usage.',
  },
  {
    value: 'sales',
    label: 'Sales and pricing',
    hint: 'Custom plans, higher volumes, paying by invoice.',
  },
  {
    value: 'abuse',
    label: 'Abuse report',
    hint: 'Report traffic from our network. Include logs with timestamps in UTC.',
  },
  { value: 'other', label: 'Something else', hint: '' },
]

export const SUPPORT_TOPIC_LABELS: Record<SupportTopic, string> =
  Object.fromEntries(SUPPORT_TOPICS.map((t) => [t.value, t.label])) as Record<
    SupportTopic,
    string
  >

const TOPIC_VALUES = SUPPORT_TOPICS.map((t) => t.value) as [
  SupportTopic,
  ...SupportTopic[],
]

/** Name of the hidden input real people never see and bots fill in. Chosen to
 *  look like a field worth completing; `autoComplete="off"` keeps password
 *  managers out of it. */
export const HONEYPOT_FIELD = 'company_url'

/** Submissions faster than this are treated as automated. Three seconds is
 *  below any realistic time to read the topic list and type a sentence. */
export const MIN_DWELL_MS = 3_000

/** Per-IP fixed window. Generous enough that a customer resending twice after a
 *  typo never notices; tight enough that a flood is pointless. */
export const SUPPORT_RATE_LIMIT = { max: 5, windowSeconds: 3600 } as const

export const MESSAGE_MAX_LENGTH = 5_000

/**
 * What the client posts. `honeypot` and `dwellMs` are part of the payload
 * rather than headers so the whole submission validates in one place.
 */
export const supportSubmissionSchema = z.object({
  topic: z.enum(TOPIC_VALUES),
  name: requiredText(100),
  email: zEmail,
  message: requiredMultilineText(MESSAGE_MAX_LENGTH),
  /** Route the form was submitted from, for triage. Anything not app-shaped is
   *  dropped rather than rejected — it is context, not input we act on. */
  pagePath: z
    .string()
    .trim()
    .max(200)
    .regex(/^\/[\w\-/?=&.]*$/, 'Invalid path.')
    .optional()
    .catch(undefined),
  honeypot: z.string().max(200).optional(),
  dwellMs: z.number().int().min(0).max(86_400_000).optional(),
})

export type SupportSubmission = z.infer<typeof supportSubmissionSchema>

/**
 * True when the advisory client-side signals say this is a bot. Kept pure and
 * exported so it is unit-testable without a request.
 */
export function looksAutomated(input: {
  honeypot?: string
  dwellMs?: number
}): boolean {
  if (input.honeypot && input.honeypot.trim().length > 0) return true
  if (input.dwellMs !== undefined && input.dwellMs < MIN_DWELL_MS) return true
  return false
}

/** Subject line for the internal alert email. */
export function supportEmailSubject(topic: SupportTopic, name: string): string {
  return `[${SUPPORT_TOPIC_LABELS[topic]}] Support request from ${name}`
}
